import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { Upload, Loader2, AlertTriangle, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

/**
 * Confirming how someone else's spreadsheet lines up with ours.
 *
 * The engine proposes a mapping; this is where a person accepts it. Nothing is
 * imported on a guess: a column the engine is unsure of has to be answered, and
 * a required column that is missing stops the import outright.
 *
 * WHAT IT DOES NOT DO is write to the database. Once the mapping is confirmed
 * the sheet is rewritten with our own headers and handed to the existing import
 * endpoint, so validation, natural keys and the transaction are the same ones
 * every other import already goes through. A second writer would eventually
 * disagree with the first, and duplicate trucks are how you find out.
 */

interface Mapping {
  header: string;
  column: string | null;
  columnLabel: string | null;
  confidence: number;
  state: 'accepted' | 'confirm' | 'none';
  reason: string;
  sample: string[];
  dataLooksLike: string;
  warning: string | null;
}
interface Proposal {
  entity: string;
  naturalKey: string | null;
  mappings: Mapping[];
  needsAttention: number;
  missingRequired: Array<{ column: string; header: string }>;
  llmCandidates: string[];
  llmAvailable: boolean;
}
interface EntityDef {
  name: string; label: string; naturalKey: string | null;
  columns: Array<{ column: string; header: string; type: string; required: boolean; readOnly?: boolean }>;
}

export function ImportPreviewModal({
  defaultEntity, onClose, onImported,
}: {
  defaultEntity?: string;
  onClose: () => void;
  onImported?: () => void;
}) {
  const qc = useQueryClient();
  const [entity, setEntity] = useState(defaultEntity ?? '');
  const [projectId, setProjectId] = useState('');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Array<Array<string | number | null>>>([]);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  /** The user's answers, keyed by the sheet's own header. */
  const [chosen, setChosen] = useState<Record<string, string>>({});
  /** Columns the user has decided not to import. Answered, not ignored. */
  const [skipped, setSkipped] = useState<Record<string, boolean>>({});
  /** Fill the driver from whoever is on that truck now. */
  const [fillDriver, setFillDriver] = useState(false);
  /**
   * Which sheet in the workbook.
   *
   * Taking the first one silently was wrong: a real file arrived with Sites,
   * Comb.Trips, Summary and daily detail, and the importer read the 10-row
   * reference tab instead of 4,958 trips - and then blamed the file for
   * missing columns.
   */
  const [book, setBook] = useState<XLSX.WorkBook | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [busy, setBusy] = useState(false);
  /** Bring back rows that were deleted on purpose - a correction, not an accident. */
  const [restoreDeleted, setRestoreDeleted] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /** What the server said was wrong, row by row. */
  const [rejects, setRejects] = useState<Array<{ row?: number; message: string }>>([]);

  const { data: entities } = useQuery({
    queryKey: ['import-entities'],
    queryFn: async () => (await apiClient.get<{ items: EntityDef[] }>('/api/v1/import-map/entities')).data.items,
  });
  const { data: projects } = useQuery({
    queryKey: ['projects-for-import'],
    queryFn: async () => (await apiClient.get<{ items: Array<{ id: string; name_en: string }> }>(
      '/api/v1/projects', { params: { pageSize: 100 } })).data.items ?? [],
  });

  const entityDef = entities?.find((e) => e.name === entity);

  const { data: onTruck } = useQuery({
    queryKey: ['driver-by-vehicle'],
    queryFn: async () => (await apiClient.get<{ items: Array<{ plate: string; iqama: string; driver: string }> }>(
      '/api/v1/import-map/driver-by-vehicle')).data.items,
  });
  /** Plates as the fleet writes them, so "5888 LTA" still finds 5888-LTA. */
  const driverFor = useMemo(() => {
    const m = new Map<string, { iqama: string; driver: string }>();
    for (const r of onTruck ?? []) {
      const key = String(r.plate ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (key) m.set(key, { iqama: r.iqama, driver: r.driver });
    }
    return m;
  }, [onTruck]);

  /** Read the workbook in the browser; the server sees it only once confirmed. */
  const readFile = async (file: File) => {
    setErr(null); setDone(null); setProposal(null); setChosen({}); setSkipped({});
    setFileName(file.name);
    const wb = XLSX.read(await file.arrayBuffer(), { cellDates: true });
    setBook(wb);
    // The biggest sheet is the data far more often than the first one is.
    const biggest = [...wb.SheetNames].sort((a, b) =>
      (XLSX.utils.sheet_to_json(wb.Sheets[b], { header: 1 }).length)
      - (XLSX.utils.sheet_to_json(wb.Sheets[a], { header: 1 }).length))[0];
    useSheet(wb, biggest);
  };

  /** Read one sheet of the open workbook. */
  const useSheet = (wb: XLSX.WorkBook, name: string) => {
    setSheetName(name);
    const rawAoa = XLSX.utils.sheet_to_json<Array<unknown>>(
      wb.Sheets[name], { header: 1, raw: true });
    /*
     * A real date cell becomes a Date and is written as YYYY-MM-DD here, at the
     * only point where the day and the month are still distinguishable. Reading
     * the displayed text instead meant "8/5/26" - which is 8 May or 5 August
     * depending on the reader's locale, and no amount of parsing downstream can
     * recover which.
     */
    const cell = (c: unknown): string | number | null => {
      if (c instanceof Date && !Number.isNaN(c.getTime())) {
        const p = (n: number) => String(n).padStart(2, '0');
        return `${c.getFullYear()}-${p(c.getMonth() + 1)}-${p(c.getDate())}`;
      }
      return (c ?? null) as string | number | null;
    };
    const aoa = rawAoa.map((r) => (r ?? []).map(cell));
    const head = (aoa[0] ?? []).map((h) => String(h ?? '').trim());
    const body = aoa.slice(1).filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
    setHeaders(head); setRows(body); setProposal(null); setChosen({}); setSkipped({});
    if (entity) void propose(entity, head, body);
  };

  const propose = async (ent: string, head: string[], body: typeof rows) => {
    if (!ent || !head.length) return;
    setBusy(true); setErr(null);
    try {
      const { data } = await apiClient.post<Proposal>('/api/v1/import-map/propose', {
        entity: ent, headers: head, sampleRows: body.slice(0, 3),
      });
      setProposal(data);
      // Start from what the engine is confident about; the rest is asked.
      setChosen(Object.fromEntries(
        data.mappings.filter((m) => m.state === 'accepted' && m.column)
          .map((m) => [m.header, m.column as string])));
    } catch (e) { setErr(extractErrorMessage(e)); }
    finally { setBusy(false); }
  };

  /** Which of our columns each sheet header will become. */
  const resolved = useMemo(() => {
    const out: Record<string, string> = {};
    for (const m of proposal?.mappings ?? []) {
      const pick = chosen[m.header] ?? (m.state === 'accepted' ? m.column ?? '' : '');
      if (pick) out[m.header] = pick;
    }
    return out;
  }, [proposal, chosen]);

  const usedColumns = new Set(Object.values(resolved));

  /**
   * How many rows carry a delivery ticket.
   *
   * The ticket is the only thing that identifies one delivery, so it is the
   * only thing that can stop a re-upload duplicating work. A sheet that is
   * mostly blank there is not protected, and the person importing should know
   * that before they press the button, not after.
   */
  const ticketCoverage = (() => {
    const header = Object.entries(resolved).find(([, col]) => col === 'receipt_number')?.[0];
    if (!header || !rows.length) return null;
    const at = headers.indexOf(header);
    if (at < 0) return null;
    const withTicket = rows.filter((r) => String(r[at] ?? '').trim() !== '').length;
    return { withTicket, total: rows.length, pct: Math.round((withTicket / rows.length) * 100) };
  })();

  /** Required columns still not covered by the user's choices. */
  const stillMissing = useMemo(
    () => (entityDef?.columns ?? []).filter((c) => c.required && !usedColumns.has(c.column)),
    [entityDef, resolved]);

  /**
   * A driver name without an iqama is the one mapping that must never pass.
   * Several drivers here share a name; the iqama is what tells them apart.
   */
  const nameWithoutIqama =
    Object.values(resolved).includes('full_name_en') && !usedColumns.has('iqama_number');

  /** The entity wants a driver iqama, the sheet has a plate, and none is mapped. */
  const iqamaCol = (entityDef?.columns ?? []).find((c) => /iqama/i.test(c.column) || /iqama/i.test(c.header));
  const plateHeader = Object.entries(resolved).find(([, col]) => /plate/i.test(col))?.[0];
  const canFillDriver = Boolean(iqamaCol && plateHeader && (onTruck?.length ?? 0) > 0
    && !usedColumns.has(iqamaCol.column));

  const unanswered = (proposal?.mappings ?? [])
    .filter((m) => m.state !== 'accepted' && !chosen[m.header] && !skipped[m.header]).length;

  const canImport = !!entity && !!projectId && !!proposal
    && (stillMissing.length === 0
        || (fillDriver && stillMissing.every((c) => c.column === iqamaCol?.column)))
    && !nameWithoutIqama && unanswered === 0 && !busy;

  /**
   * Rewrite the sheet under our own headers and hand it to the import engine.
   * The mapping is the only thing this screen contributes; everything after is
   * the path every other import already takes.
   */
  const runImport = async () => {
    if (!entityDef) return;
    setBusy(true); setErr(null); setRejects([]);
    try {
      /*
       * The engine checks the sheet against its own template before it reads a
       * single row: every column, in its order, or the file is refused whole.
       * So the workbook is rebuilt as the full template, with the mapped
       * columns filled and the rest left blank - not as a sheet of only the
       * columns that happened to map.
       */
      const outHead = entityDef.columns.map((c) => c.header);
      const sourceFor = entityDef.columns.map((c) => {
        const from = Object.entries(resolved).find(([, col]) => col === c.column)?.[0];
        return from ? headers.indexOf(from) : -1;
      });
      const body = rows.map((r) => sourceFor.map((i) => (i >= 0 ? r[i] ?? '' : '')));

      /*
       * Plates, written the way the yard writes them.
       *
       * "5888 LTA", "5888lta", " 5888 - LTA " are all the same truck; the
       * database keeps one form, 5888-LTA. Only reshaped when it is
       * unmistakably four digits and three letters - anything else is passed
       * through untouched so the engine reports it rather than this quietly
       * inventing a plate.
       */
      const plateAtOut = entityDef.columns
        .map((c, i) => (/plate/i.test(c.column) ? i : -1)).filter((i) => i >= 0);
      if (plateAtOut.length) {
        for (const row of body) {
          for (const i of plateAtOut) {
            const raw = String(row[i] ?? '').trim().toUpperCase();
            const m = raw.replace(/[\s_.-]+/g, '').match(/^(\d{4})([A-Z]{3})$/);
            if (m) row[i] = `${m[1]}-${m[2]}`;
          }
        }
      }

      // The driver from the truck. Done here, on the way out, so what is sent is
      // exactly what was shown - no hidden column appearing after review.
      if (fillDriver && iqamaCol && plateHeader) {
        const iqamaAt = entityDef.columns.findIndex((c) => c.column === iqamaCol.column);
        const plateAt = headers.indexOf(plateHeader);
        for (const row of body) {
          const key = String(rows[body.indexOf(row)]?.[plateAt] ?? '')
            .toUpperCase().replace(/[^A-Z0-9]/g, '');
          const found = driverFor.get(key);
          if (found && iqamaAt >= 0) row[iqamaAt] = found.iqama;
        }
      }
      /*
       * Dates, as spreadsheets actually write them.
       *
       * "5-Aug-26", "05/08/2026" and a real Excel date cell are all the same
       * day; the engine takes YYYY-MM-DD. Converted only when the shape is
       * unmistakable - anything else passes through so the engine says so
       * rather than this guessing at day-versus-month.
       */
      const MONTHS: Record<string, string> = {
        jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
        jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
      };
      const toIso = (raw: string): string => {
        const v = raw.trim();
        if (!v || /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
        // 5-Aug-26 / 05 Aug 2026
        const m = v.match(/^(\d{1,2})[\s./-]+([A-Za-z]{3,})[\s./-]+(\d{2,4})$/);
        if (m) {
          const mm = MONTHS[m[2].slice(0, 3).toLowerCase()];
          if (mm) {
            const yy = m[3].length === 2 ? `20${m[3]}` : m[3];
            return `${yy}-${mm}-${m[1].padStart(2, '0')}`;
          }
        }
        // 2026-08-05T00:00:00Z from a real date cell
        const iso = v.match(/^(\d{4})-(\d{2})-(\d{2})T/);
        if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
        // 8/5/26 is 8 May or 5 August and nothing here can tell which. Left
        // alone so the engine refuses it, rather than guessing on a date that
        // decides which month a driver is paid for.
        return v;
      };
      const dateAtOut = entityDef.columns
        .map((c, i) => (c.type === 'date' ? i : -1)).filter((i) => i >= 0);

      /*
       * Numeric dates, decided by the column rather than the cell.
       *
       * "8/5/26" alone is 8 May or 5 August. But a column of them almost always
       * gives itself away: one value with a first part above 12 can only be a
       * day, one with a second part above 12 can only be a day in the second
       * slot. So the whole column is read first, the order is settled once, and
       * then every cell is converted the same way.
       *
       * If a column never exceeds 12 in either slot it is genuinely undecidable
       * and is left alone for the engine to refuse - guessing there would
       * quietly move a month of work into the wrong month.
       */
      const NUM = /^(\d{1,4})[/.-](\d{1,2})[/.-](\d{2,4})$/;
      for (const i of dateAtOut) {
        let firstIsDay = false, firstIsMonth = false;
        for (const row of body) {
          const m = String(row[i] ?? '').trim().match(NUM);
          if (!m || m[1].length === 4) continue;
          if (Number(m[1]) > 12) firstIsDay = true;
          if (Number(m[2]) > 12) firstIsMonth = true;
        }
        // Contradictory evidence means the column is not one format; leave it.
        if (firstIsDay && firstIsMonth) continue;
        if (!firstIsDay && !firstIsMonth) continue;
        for (const row of body) {
          const v = String(row[i] ?? '').trim();
          const m = v.match(NUM);
          if (!m || m[1].length === 4) continue;
          const d = firstIsDay ? m[1] : m[2];
          const mo = firstIsDay ? m[2] : m[1];
          const y = m[3].length === 2 ? `20${m[3]}` : m[3];
          if (Number(mo) < 1 || Number(mo) > 12 || Number(d) < 1 || Number(d) > 31) continue;
          row[i] = `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
      }

      for (const row of body) {
        for (const i of dateAtOut) row[i] = toIso(String(row[i] ?? ''));
      }

      const aoa = [outHead, ...body];
      const wb = XLSX.utils.book_new();
      /*
       * The engine checks two things before it reads a row: a sheet called
       * Data, and a `_jasr` sheet naming the entity and its exact column list.
       * Without them the file is refused as "not downloaded from JASR Hub" -
       * which is what an outside vendor's sheet always is. Writing them here is
       * what turns their file into ours.
       */
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['DO NOT EDIT OR DELETE THIS SHEET'],
        ['entity', entity],
        ['columns', outHead.join('|')],
        ['exportedAt', new Date().toISOString()],
      ]), '_jasr');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), 'Data');
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;

      const fd = new FormData();
      fd.append('file', new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }), `mapped-${fileName || 'import.xlsx'}`);

      /*
       * The side goes in the query string, not a header.
       *
       * The import route reads `project` off the query; sending X-Project-Id
       * looked right and did nothing, and 94 trips landed with no side - which
       * makes them invisible on every page that scopes by one. Both are sent
       * now: the query is what this endpoint reads, the header is what the rest
       * of the app expects.
       */
      const { data } = await apiClient.post<Record<string, unknown>>(
        `/api/v1/excel/${entity}/import?project=${encodeURIComponent(projectId)}`
          + (restoreDeleted ? '&restoreDeleted=true' : ''), fd,
        { headers: { 'X-Project-Id': projectId } });

      const created = Number(data.created ?? data.inserted ?? 0);
      const updated = Number(data.updated ?? 0);
      setDone(`Imported ${created} new and updated ${updated} existing record(s).`);
      void qc.invalidateQueries();
      onImported?.();
    } catch (e) {
      /*
       * A 422 is the engine having read every row and refused some. The bare
       * status is useless to whoever has to fix the sheet; the report says
       * which row and why, so that is what gets shown.
       */
      const res = (e as { response?: { status?: number; data?: unknown } }).response;
      const data = res?.data as Record<string, unknown> | undefined;
      const raw = (data?.errors ?? data?.issues ?? data?.rows ?? data?.details) as unknown;
      const list: Array<{ row?: number; message: string }> = Array.isArray(raw)
        ? raw.slice(0, 40).map((x) => {
            const o = x as Record<string, unknown>;
            return {
              row: Number(o.row ?? o.rowNumber ?? o.index) || undefined,
              message: String(o.message ?? o.error ?? o.reason ?? JSON.stringify(o)).slice(0, 200),
            };
          })
        : [];
      setRejects(list);
      setErr(list.length
        ? `The sheet was read but ${Array.isArray(raw) ? raw.length : list.length} row(s) were refused.`
        : (data?.message as string) ?? (data?.error as string) ?? extractErrorMessage(e));
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         role="dialog" aria-modal="true" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-card">
          <div className="font-medium">Import a spreadsheet</div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* what, and whose */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">What is this a list of?</label>
              <select value={entity}
                onChange={(e) => { setEntity(e.target.value); setProposal(null); setChosen({}); setSkipped({});
                  if (headers.length) void propose(e.target.value, headers, rows); }}
                className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option value="">— pick —</option>
                {(entities ?? []).map((e) => (
                  <option key={e.name} value={e.name}>{e.label ?? e.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                Side <span className="text-rose-500">*</span>
              </label>
              <select value={projectId} onChange={(e) => setProjectId(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                <option value="">— pick a side —</option>
                {(projects ?? []).map((p) => <option key={p.id} value={p.id}>{p.name_en}</option>)}
              </select>
              {/* Rows filed under no side become invisible to every page that
                  scopes by it, which has happened here before. */}
              <p className="text-[11px] text-muted-foreground">Rows without a side disappear from every page.</p>
            </div>
          </div>

          <label className="flex items-center justify-center gap-2 h-16 rounded-lg border border-dashed border-border text-sm cursor-pointer hover:bg-muted/40 transition">
            <Upload className="h-4 w-4" />
            {fileName || 'Choose an .xlsx or .csv file'}
            <input type="file" accept=".xlsx,.xls,.csv" className="sr-only"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void readFile(f); e.target.value = ''; }} />
          </label>

          {book && book.SheetNames.length > 1 && (
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Which sheet?</label>
              <select value={sheetName} onChange={(e) => useSheet(book, e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                {book.SheetNames.map((n) => {
                  const count = XLSX.utils.sheet_to_json(book.Sheets[n], { header: 1 }).length - 1;
                  return <option key={n} value={n}>{n} — {count > 0 ? `${count} rows` : 'empty'}</option>;
                })}
              </select>
              <p className="text-[11px] text-muted-foreground">
                This workbook has {book.SheetNames.length} sheets. The largest is picked; change it if that is wrong.
              </p>
            </div>
          )}

          {headers.length > 0 && (
            <div className="text-xs text-muted-foreground">
              Reading <b className="text-foreground">{sheetName}</b> — {rows.length} row(s), {headers.length} column(s)
            </div>
          )}

          {busy && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Working…
            </div>
          )}
          {err && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-600 dark:text-rose-400">
              {err}
            </div>
          )}
          {rejects.length > 0 && (
            <div className="rounded-md border border-rose-500/30 max-h-56 overflow-y-auto divide-y divide-border">
              {rejects.map((r, i) => (
                <div key={i} className="px-3 py-1.5 text-xs flex gap-2">
                  {r.row ? <span className="font-mono text-muted-foreground shrink-0">row {r.row}</span> : null}
                  <span>{r.message}</span>
                </div>
              ))}
            </div>
          )}

          {done && (
            <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-2">
              <Check className="h-4 w-4" /> {done}
            </div>
          )}

          {proposal && (
            <>
              {/* the two things that stop an import outright */}
              {stillMissing.length > 0 && (
                <div className="rounded-md bg-rose-500/10 border border-rose-500/40 p-3 text-sm">
                  <div className="font-medium text-rose-700 dark:text-rose-400 inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" /> Required columns are missing
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Nothing can be imported until these are mapped:{' '}
                    <b className="text-foreground">{stillMissing.map((c) => c.header).join(', ')}</b>
                  </div>
                </div>
              )}
              {canFillDriver && (
                <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm cursor-pointer">
                  <input type="checkbox" checked={fillDriver} className="mt-0.5"
                    onChange={(e) => setFillDriver(e.target.checked)} />
                  <span>
                    Take the driver from the truck
                    <span className="block text-xs text-muted-foreground">
                      Fills {iqamaCol?.header} from whoever is assigned to each plate now
                      ({onTruck?.length ?? 0} trucks have a driver). This is the CURRENT driver —
                      for older trips, whoever drove that day may be someone else.
                    </span>
                  </span>
                </label>
              )}

              {/* Deleting to replace is a normal thing to do; without this the
                  corrected rows are skipped and it looks like the import failed. */}
              <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm cursor-pointer">
                <input type="checkbox" checked={restoreDeleted} className="mt-0.5"
                  onChange={(e) => setRestoreDeleted(e.target.checked)} />
                <span>
                  Bring back rows I deleted earlier
                  <span className="block text-xs text-muted-foreground">
                    Normally a deleted delivery ticket stays deleted, so re-uploading an old file
                    cannot resurrect it. Tick this when you deleted rows in order to replace them
                    with corrected ones.
                  </span>
                </span>
              </label>

              {ticketCoverage && ticketCoverage.pct < 100 && (
                <div className={`rounded-md p-3 text-sm border ${ticketCoverage.pct < 10
                  ? 'bg-amber-500/10 border-amber-500/40'
                  : 'bg-muted/40 border-border'}`}>
                  <div className="font-medium">
                    {ticketCoverage.withTicket} of {ticketCoverage.total} rows carry a delivery ticket
                    {' '}({ticketCoverage.pct}%)
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Rows without a ticket cannot be recognised on a later upload, so re-importing this
                    file would add them again. Rows with one are matched and updated instead.
                  </div>
                </div>
              )}

              {nameWithoutIqama && (
                <div className="rounded-md bg-amber-500/10 border border-amber-500/40 p-3 text-sm">
                  <div className="font-medium text-amber-700 dark:text-amber-400">Map the iqama column</div>
                  <div className="text-muted-foreground mt-1">
                    A driver name identifies nobody here — several drivers share one. Map the iqama
                    or national ID column before importing.
                  </div>
                </div>
              )}

              <div className="text-xs text-muted-foreground">
                Plates written as “5888 LTA” or “5888lta” are saved as <b className="text-foreground">5888-LTA</b>.
              </div>

              <div className="text-xs text-muted-foreground">
                {rows.length} row(s) · {proposal.naturalKey
                  ? <>matched on <b className="text-foreground">{proposal.naturalKey}</b>, so re-uploading updates rather than duplicates</>
                  : 'this list has no natural key — rows will be added'}
              </div>

              {/* every column, and what it will become */}
              <div className="rounded-lg border border-border divide-y divide-border">
                {proposal.mappings.map((m) => {
                  const pick = chosen[m.header] ?? (m.state === 'accepted' ? m.column ?? '' : '');
                  const tone = skipped[m.header] ? 'border-l-border'
                    : pick ? 'border-l-emerald-500'
                    : m.state === 'confirm' ? 'border-l-amber-500' : 'border-l-rose-500';
                  return (
                    <div key={m.header} className={`p-3 border-l-2 ${tone}`}>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <div className="font-mono text-sm">{m.header}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {m.sample.filter(Boolean).slice(0, 3).join('  ·  ') || 'no sample values'}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <select value={pick}
                          onChange={(e) => setChosen((c) => ({ ...c, [m.header]: e.target.value }))}
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm min-w-[14rem]">
                          <option value="">— not imported —</option>
                          {(entityDef?.columns ?? []).filter((c) => !c.readOnly).map((c) => (
                            <option key={c.column} value={c.column}
                              disabled={usedColumns.has(c.column) && pick !== c.column}>
                              {c.header}{c.required ? ' *' : ''}
                            </option>
                          ))}
                        </select>
                        {m.state !== 'accepted' && !pick && !skipped[m.header] && (
                          <button type="button"
                            onClick={() => setSkipped((s) => ({ ...s, [m.header]: true }))}
                            className="text-xs text-muted-foreground hover:text-foreground underline">
                            leave this column out
                          </button>
                        )}
                        <span className="text-[11px] text-muted-foreground">{m.reason}</span>
                      </div>
                      {m.warning && (
                        <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">{m.warning}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {proposal.llmCandidates.length > 0 && !proposal.llmAvailable && (
                <p className="text-[11px] text-muted-foreground">
                  {proposal.llmCandidates.length} column(s) could not be matched by name. An AI key on the
                  server would let the system suggest these; without one they are answered by hand.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border sticky bottom-0 bg-card">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="gold" disabled={!canImport} onClick={runImport}>
            {busy ? 'Importing…' : 'Confirm and import'}
          </Button>
        </div>
      </div>
    </div>
  );
}
